import type { Catalogue } from "./fr";

/** Same shape as the French catalogue, with values widened to `string`.
 *  A missing or extra key here is a compile error. */
export type Translations = {
  [K in keyof Catalogue]: Catalogue[K] extends string
    ? string
    : { [P in keyof Catalogue[K]]: string };
};

export const en: Translations = {
  common: {
    team: "Team",
    region: "Region",
    prefix: "Prefix",
    accessKey: "Access key",
    secretKey: "Secret key",
    host: "Host",
    port: "Port",
    endpoint: "Address",
    expiresAt: "Expires",
    expired: "Expired",
    revoke: "Revoke",
    createToken: "Create a token",
    neverUsed: "never used",
    actions: "Actions",
    add: "Add",
    cancel: "Cancel",
    close: "Close",
    copy: "Copy",
    create: "Create",
    delete: "Delete",
    edit: "Edit",
    error: "Error",
    export: "Export",
    loading: "Loading…",
    name: "Name",
    description: "Description",
    open: "Open",
    owner: "Owner",
    reader: "Reader",
    writer: "Writer",
    save: "Save",
    search: "Search",
    settings: "Settings",
    status: "Status",
    createdAt: "Created",
    updatedAt: "Updated",
    startedAt: "Started",
    duration: "Duration",
    project: "Project",
    type: "Type",
    size: "Size",
    role: "Role",
    user: "User",
    organization: "Organisation",
    none: "None",
    yes: "Yes",
    no: "No",
    required: "This field is required.",
    accessDenied: "You do not have permission to perform this action.",
    viewLogs: "View logs",
    logs: "Logs",
  },

  nav: {
    componentCredentials: "Component credentials",
    serviceAccounts: "Service accounts",
    administrator: "Admin",
    home: "Home",
    projects: "Projects",
    catalog: "Catalog",
    agents: "Agents",
    production: "Production",
    administration: "Administration",
    overview: "Overview",
    workspaces: "Workspaces",
    jobs: "Jobs",
    apps: "Apps",
    apis: "APIs",
    dashboards: "Dashboards",
    data: "Data",
    environments: "Environments",
    members: "Members",
    settings: "Settings",
    datasets: "Datasets",
    datasources: "Data sources",
    ontologies: "Ontologies",
    repositories: "Git repositories",
    network: "Network and security",
    rbac: "Access",
    activity: "Activity",
    audit: "Audit",
    backups: "Backups",
    identity: "Users and organisations",
    dataGovernance: "Data governance",
    inventory: "Software and licences",
    storage: "Storage",
    backToProjects: "All projects",
    documentation: "Documentation",
    apiReference: "API reference",
    toggleSidebar: "Collapse menu",
    theme: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    language: "Language",
    signIn: "Sign in",
    signOut: "Sign out",
    skipToContent: "Skip to main content",
  },

  storageCapacity: {
    title: "Room left for volumes",
    intro:
      "What the cluster can still accept. A volume claim is accepted on sight and only fails at attach time: without this screen, the first warning is a user who cannot work.",
    claimedOf: "claimed of {total} allocatable",
    freeDisk: "{free} of disk actually unused",
    leftToAllocate: "{left} left to allocate",
    nodeDetail: "{claimed} claimed of {allocatable} · {free} of free disk",
    tight: "Filling up",
    full: "Full",
    unavailable:
      "This installation runs on a storage layer that cannot be asked. The platform therefore cannot warn in advance — it says so rather than drawing a gauge it made up.",
    explain:
      "A volume is placed against what it claims, not against free disk. That is why the two figures differ, and why a nearly empty cluster can refuse one more volume.",
  },
  platformRoles: {
    title: "What a role may create",
    intro:
      "Functions in rows, roles in columns. Three roles ship: the administrator acts on what is not theirs, the user on their own, the consultant reads. Their names are the platform's, their boxes are yours - except the administrator's, the row this installation falls back on. Add your own roles beside them. What anybody may read stays a grant, on the object itself.",
    function: "Function",
    heldBy: "held by {count}",
    changed: "Changed here",
    shipped: "Shipped",
    notAssignable: "Directory role",
    addRole: "Add a role",
    keyPlaceholder: "data-steward",
    namePlaceholder: "Data steward",
    descriptionPlaceholder: "What this role does, in one sentence",
    save: "Save",
    saved: "Role saved",
    remove: "Remove",
    removed: "Role removed",
    role: "Role",
    adminHint: "Platform administrator: a directory role, acting on what is not theirs. Named and withdrawn here by another administrator; never oneself, never the last one.",
    signInAgain: "The person must sign in again for the role to take effect.",
    withdrawAdmin: "Withdraw the administrator role",
    roleHint: "What this person may create. With no assignment, that is the user role.",
    assign: "Assign",
    assigned: "Role assigned",
    reset: "Back to user",
    defaultRole: "default",
    "create-project": "Create a project",
    "launch-workspace": "Launch a workspace",
    "launch-job": "Launch a job",
    "deploy-app": "Deploy an application",
    "build-environment": "Build an environment",
    "create-dataset": "Create a dataset",
    "scan-ontology": "Scan an ontology",
    "create-extract": "Create an extract",
    "declare-datasource": "Declare a data source",
    "declare-repository": "Declare a repository",
    "register-regulated-dataset": "Register a regulated dataset",
    "attach-regulated-data": "Attach regulated data to a project",
    "administer-organization": "Administer an organization",
  },
  roleMatrix: {
    title: "Project roles",
    intro:
      "What a role given in a project allows: read or change the project and its members, attach data, launch workloads, manage environments. Never read or write the data itself: that is a grant on the object, and attaching does not give it. Three roles ship locked; your own are written here and given on the members screen.",
    addRole: "Add a role",
    basedOn: "Based on",
    namePlaceholder: "Data steward",
    descriptionPlaceholder: "What this role does, in one sentence",
    shipped: "Shipped",
    columnProject: "Project",
    columnDataset: "Attach datasets",
    columnOntology: "Attach ontologies and extracts",
    columnDatasource: "Attach data sources",
    columnEnvironment: "Manage environments",
    columnWorkload: "Launch workloads",
    yes: "yes",
    no: "no",
    baseViewer: "Viewer",
    baseEditor: "Editor",
    baseAdmin: "Administrator",
    shippedBase: "Platform role",
    heldBy: "held by {count}",
    saved: "Roles saved",
    ceilingHint:
      "A role of your own always inherits from a platform role: that is what the person gets wherever the matrix has nothing to say, and it is also their ceiling until the matrix enforces the row.",
  },
  agentGovernance: {
    title: "Agents",
    intro:
      "What the agents of this installation may do, and what they did. Activity is counted from the actions the platform recorded, never from what their reports claim.",
    agents: "Agents",
    canAct: "May act",
    canActHint: "The others look and report",
    mandates: "Allowed asks",
    actions: "Actions taken",
    window: "Over {days} days",
    mayDo: "May act",
    state: "State",
    activity: "Activity",
    runsAndActions: "{runs} runs \u00b7 {actions} actions",
    lastRun: "Last run",
    noAgents: "No agent on this installation",
    noAgentsHint:
      "Agents are recruited inside a project, and appear here once they exist.",
    organisationIntro:
      "Written in advance: a lead may ask a member to act only where the line exists here. A line that is saved is a line that works.",
    noMandates: "Nobody may ask anybody for anything.",
    grantedBy: "Allowed by {who}, {when}",
  },
  agents: {
    tabAgents: "Agents",
    tabWorkflows: "Workflows",
    tabTeams: "Teams",
    statAgents: "Agents",
    statAgentsHint: "{active} at work out of {total}",
    statWorkflows: "Workflows",
    statWorkflowsHint: "{active} in service out of {total}",
    statDecisions: "To decide",
    statDecisionsHint: "Runs are waiting for a person",
    statDecisionsNone: "Nothing is waiting for anybody",
    statGrants: "May act",
    statGrantsHint: "{granted} may act, {readers} look and report",
    inboxTitle: "To decide",
    inboxIntro:
      "These runs are waiting for a named person. They resume as soon as you decide, and do nothing until then.",
    inboxStep: "step {index} · {step}",
    grants: "May act",
    conceptLead:
      "An agent is what you see: a colleague you leave a standing instruction with. A workflow is what runs: a sequence of steps, each given to the model or to a person. An agent today is a one-step workflow.",
    title: "Agents",
    intro:
      "Standing instructions, in your own words. An agent watches for you and tells you what it saw.",
    recruit: "Hire",
    recruitFirst: "Hire my first agent",
    recruitTitle: "Hire an agent",
    editTitle: "Edit the agent",
    recruitIntro:
      "Give them a name and say what you expect, the way you would to somebody starting.",
    nameLabel: "Their name",
    namePlaceholder: "Clemence",
    missionLabel: "Their assignment",
    missionHelp:
      "Write it in plain language. Nothing here is a keyword: the assignment is passed on as written.",
    missionPlaceholder:
      "Watch my workspaces. When one will not start, tell me which and why, in one sentence.",
    examplesTitle: "For example:",
    exampleWorkspaces:
      "Watch my workspaces. When one will not start, tell me which and why, in one sentence.",
    exampleApps:
      "Check my applications. If one has stopped, restart it and tell me.",
    exampleQuiet: "Once a day, go round my projects and tell me what changed.",
    scheduleLabel: "Their hours",
    scheduleManual: "On demand",
    scheduleHourly: "Every hour",
    scheduleDaily: "Once a day",
    mayRestartLabel: "They may restart a stopped application",
    mayRestartHelp:
      "Without this they look and report, and change nothing. It is the only thing an agent can change, and every restart is recorded.",
    mayRestart: "May restart an application",
    mayCallApiLabel: "They may act on the platform through its API",
    mayCallApiHelp:
      "Launch a job, write into a dataset, declare an extract, stop an application: anything you can do yourself, in your name and with your rights. Every call is recorded.",
    mayCallApi: "May act through the API",
    readsOnly: "Looks and reports",
    hire: "Hire",
    hired: "{name} has started.",
    updated: "Updated.",
    teamsTitle: "Teams",
    teamsIntro:
      "Several agents on one subject. You write down in advance who may ask whom for what, and nothing else happens.",
    teamsEmpty:
      "Your agents each work on their own. A team gives them a shared subject and lets you write what they may ask of each other.",
    formTeam: "Form a team",
    formFirstTeam: "Form my first team",
    formTeamTitle: "Form a team",
    formTeamIntro: "Give it a name and say what it works on.",
    teamNameLabel: "Its name",
    teamNamePlaceholder: "On call",
    teamPurposeLabel: "Its subject",
    teamPurposePlaceholder:
      "Keep the production applications running overnight.",
    teamFormed: "The {name} team is formed.",
    teamMembers: "Who is in it",
    teamNoMembers: "Nobody yet. Put an agent in this team from its own card.",
    organisation: "Who may ask for what",
    organisationHelp:
      "A line that is saved is a line that works: if the ask could never succeed, it is refused straight away with its reason.",
    organisationEmpty:
      "Nobody may ask anybody for anything yet. A lead may ask a member to act, once you have written it here.",
    mandateSentence: "{lead} may ask {member} to {action}.",
    askActionRestartApp: "restart a stopped application",
    askActionCallApi: "act on the platform through its API",
    allowAsk: "Allow an ask",
    allowAskTitle: "Allow an ask",
    allowAskIntro:
      "Choose who asks and whom. This grants no new right: it only says who may ask for it.",
    askLeadLabel: "Who asks",
    askMemberLabel: "Whom",
    askActionLabel: "To do what",
    askWritten: "Written down.",
    withdraw: "Withdraw",
    withdrawn: "Withdrawn.",
    needsALead:
      "The team needs at least one lead. An agent becomes a lead from its own card.",
    roleObserver: "Looks and reports",
    roleOperator: "May act",
    roleLead: "Lead",
    roleLabel: "Its role in the team",
    teamLabel: "Its team",
    noTeam: "Works alone",
    dismiss: "Dismiss",
    orphansTitle: "Agents with no project",
    orphansHint:
      "They appear on no project page and no longer run. Dismiss them, or recreate them inside a project.",
    dismissTitle: "Dismiss {name}?",
    dismissBody:
      "Its assignment and its journal are deleted. What it restarted or reported stays in the platform audit.",
    dismissed: "{name} has been dismissed.",
    ask: "Ask",
    askPlaceholder: "Ask {name} something\u2026",
    youAsked: "You:",
    runNow: "Do it now",
    atWork: "At work",
    paused: "Paused",
    never: "Never worked",
    justNow: "Just now",
    minutesAgo: "{count} min ago",
    hoursAgo: "{count} h ago",
    daysAgo: "{count} d ago",
    whatTheySaw: "What {name} saw",
    nothingToReport: "Nothing to report",
    reported: "They have something to say.",
    failed: "Failed",
    loadingRuns: "Reading the journal…",
    neverRan:
      "They have not worked yet. Run them now, or wait for their hours.",
    emptyLead:
      "You have not hired anybody yet. An agent is somebody you leave a standing instruction with, written as you would say it:",
    actionRestartApp: "Application restarted",
    actionCallApi: "API call {call}",
  },
  workflows: {
    pipelineTitle: "The sequence",
    triggerNode: "Trigger",
    reportNode: "Report",
    whenTitle: "When",
    whenInWords: 'Say when, in words: "every morning at 8 except weekends"',
    soon: "Soon",
    onEvent: "On an event",
    eventJobFinished: "When a job finishes",
    eventDatasetChanged: "When a dataset changes",
    stepVerbs: "Verbs",
    stepReadsOnly: "Looks and reports",
    edit: "Edit",
    currentInstruction: "The instruction of the current step",
    title: "Workflows",
    intro:
      "What runs when an agent works: a sequence of steps, each handed to the model or to a named person. The sequence decides what comes next, not the model.",
    create: "Write a workflow",
    createFirst: "Write the first one",
    emptyLead:
      "A workflow chains steps. An agent looks, a person reviews, a second agent analyses what the first one found — and every step is written down before the next one starts.",
    editTitle: "A workflow",
    createIntro: "A name, hours, and the steps in the order they follow.",
    nameLabel: "Name",
    namePlaceholder: "Watch, then analyse",
    scheduleLabel: "Hours",
    stepsLabel: "The steps, in order",
    stepsHelp:
      "An agent step receives its instruction and what the previous step produced. An approval step waits for a person and consumes nothing.",
    addAgentStep: "Add an agent step",
    addApprovalStep: "Add an approval",
    stepAgent: "Agent",
    stepApproval: "Approval",
    stepNameLabel: "Title",
    stepNamePlaceholder: "Look",
    stepInstructionLabel: "Instruction",
    stepInstructionPlaceholder:
      "Look for what is new on the subject and say it in three sentences.",
    stepApproverLabel: "Who approves",
    stepApproverHelp:
      "The person’s identifier, a member of the project. Only they will be able to let it through or stop it.",
    stepMayRestart: "May restart an application",
    stepMayCallApi: "May act through the API",
    removeStep: "Remove this step",
    moveUp: "Move up",
    moveDown: "Move down",
    save: "Write",
    written: "{name} is written.",
    deleted: "{name} is deleted, with its runs.",
    delete: "Delete",
    deleteTitle: "Delete {name}?",
    deleteBody:
      "The workflow and all its runs disappear. What its steps did stays in the platform audit.",
    runNow: "Run now",
    started: "Started.",
    missionTitle: "What it is for",
    whereItIs: "Where it is",
    idle: "Nothing in progress",
    atStep: "At step {index} — {name}",
    lastRun: "Last run",
    nextRun: "Next run",
    nextManual: "When somebody runs it",
    nextHourly: "Within the hour",
    nextDaily: "Within the day",
    runsTitle: "Runs",
    loadingRuns: "Reading the runs…",
    neverRan: "It has not run yet.",
    approve: "Let it through",
    reject: "Stop it",
    approved: "The run continues.",
    rejected: "The run is stopped.",
    waitingFor: "Waiting for {name}",
    attempts: "{count} attempt(s)",
    statusPending: "Pending",
    statusRunning: "Running",
    statusWaiting: "Waiting for a person",
    statusSucceeded: "Done",
    statusFailed: "Failed",
    statusCancelled: "Stopped",
    stepUntitled: "Untitled step",
    editHint: "Click + on an arrow to insert a step, or a step to edit it.",
    insertHere: "Insert a step here",
    moveLeft: "Move earlier",
    moveRight: "Move later",
    updated: "{name} is updated.",
    triggerTitle: "The trigger",
    triggerHelp: "When the run starts. The steps follow, in order.",
    editing: "Editing",
    stepKindLabel: "Kind of step",
    canvasTitle: "The run",
    inspectorTitle: "The step",
    selectStep: "Click a step to read it.",
    stepStatusTitle: "Where it is",
    stepInstructionTitle: "Its instruction",
    stepOutput: "What it produced",
    stepActions: "What the platform saw it do",
    stepError: "Why it failed",
    stepNoRun: "Not run yet.",
  },
  aiServices: {
    title: "AI services",
    full: "Full service",
    degraded: "Reduced",
    down: "Stopped",
  },
  home: {
    title: "Hello {name}",
    subtitle: "Pick up where you left off, or start a new project.",
    recentProjects: "Recent projects",
    recentProjectsHint: "Your most recently active projects.",
    quickActions: "Get started",
    newProject: "New project",
    newProjectHint: "Create a collaborative workspace",
    browseCatalog: "Browse the catalog",
    browseCatalogHint: "Datasets, data sources and ontologies",
    platformActivity: "Platform activity",
    users: "Users",
    usersHint: "Accounts on the platform",
    projects: "Projects",
    projectsHint: "Collaborative spaces",
    workloads: "Workloads",
    workloadsHint: "Active workspaces, jobs and apps",
    storage: "Storage",
    storagePartial:
      "Across {measured} datasets. {pending} not measured yet, so the real volume is higher.",
    storagePending: "Not measured yet - the first sweep runs tonight.",
    storageAsOf: "Across all {count} datasets, measured {when}.",
    storageUnreadable:
      "Across {measured} datasets. {unreadable} could not be read: until that is fixed this total is short.",
    storageHint: "Registered on the platform",
    noProjects: "No projects yet",
    noProjectsHint:
      "A project holds your workspaces, runs, data and the people working on them.",
  },

  projects: {
    deleteHoldsWarning:
      "This project still holds {count} item(s), {running} of them running. Deleting them cannot be undone.",
    deleteHoldsConfirm:
      "You are about to destroy the project and the {count} item(s) listed, {running} of them running. Type the project name to confirm.",
    storageSizeLabel: "Workspace capacity",
    storageSizeHint:
      "Volume mounted at /mnt in every workspace of this project, kept across restarts. Workspaces already running keep the volume they have.",
    storageSizeDefault: "Platform default",
    title: "Projects",
    subtitle: "A project holds your workspaces, runs, data and collaborators.",
    create: "New project",
    createTitle: "Create a project",
    createHint:
      "A name and a description are enough to start. Everything else is configured later.",
    nameLabel: "Project name",
    nameHint: "Visible to every member. You can change it later.",
    namePlaceholder: "Sales analysis 2026",
    descriptionHint:
      "One sentence about the goal of the project, for your collaborators.",
    search: "Search projects",
    empty: "No projects",
    emptyHint: "Create your first project to start working.",
    lastActivity: "Last activity",
    resources: "Active resources",
    deleteTitle: "Delete project",
    deleteWarning:
      "This cannot be undone. Workspaces, jobs and apps in this project will be stopped and deleted.",
    transferOwnership: "Transfer ownership",
    ownedByUser: "Owned by a person",
    ownedByOrganization: "Owned by an organization",
    ownerCurrent: "Current owner:",
    ownerUnchangedHint: "That is the current owner. Pick somebody else to transfer.",
    ownerTypeLabel: "Owner type",
    ownerLabel: "New owner",
    viewMode: "View",
    viewGrid: "Cards",
    viewList: "List",
  },

  projectOverview: {
    title: "Overview",
    subtitle: "Current state of the project and shortcuts to its resources.",
    activeWorkspaces: "Active workspaces",
    runningJobs: "Running jobs",
    runningApps: "Running apps",
    attachedDatasets: "Attached datasets",
    quickLaunch: "Launch a workspace",
    recentActivity: "Recent activity",
    noActivity: "No recent activity on this project.",
    settingsTitle: "Project settings",
    settingsHint: "Project name, description and owner.",
  },

  workspaces: {
    environmentUnknown: "Environment removed",
    rebuiltSince:
      "The environment was rebuilt since this was started — relaunch to get the new image.",
    rebuiltSinceShort: "rebuilt since",
    assistantSendsDataOffSite:
      "This project mounts health data ({datasets}). The assistant reads workspace files to answer you, and what it reads is sent to the model, which is hosted outside the site.",
    startupTitle: "Starting",
    startupHint:
      "The main steps of the launch. This panel disappears once the workspace is open.",
    startupStuck: "The start has stopped: it will not resume on its own.",
    startupTechnical: "Technical detail",
    step_requested: "Request recorded",
    step_scheduled: "Machine reserved",
    step_image: "Environment fetched",
    step_storage: "Data attached",
    step_environment: "Preparing the session",
    reason_no_capacity: "No machine can take this size at the moment.",
    reason_image_unavailable: "The environment image could not be fetched.",
    reason_storage_unavailable:
      "The data storage could not be attached. Tell your administrator.",
    reason_environment_failed:
      "The session stopped while it was being prepared.",
    reason_environment_out_of_disk:
      "The session wrote more than its temporary space allows. Put environments and caches in /mnt, ~/.noryx-profile or /datasets — only those survive a stop.",
    reason_environment_restarting: "The session keeps restarting.",
    reason_environment_out_of_memory:
      "Closed because it ran out of memory. Relaunch it on a larger machine size.",
    title: "Workspaces",
    subtitle: "Your interactive working environments on this project.",
    create: "New workspace",
    createTitle: "Launch a workspace",
    createHint:
      "Pick an environment and a machine size. The workspace starts within seconds.",
    environmentLabel: "Environment",
    environmentHint: "Determines the available libraries and the IDE used.",
    tierLabel: "Machine size",
    tierHint: "You can relaunch the workspace on a different size at any time.",
    nameLabel: "Workspace name",
    nameHint: "Helps you tell them apart if you run several.",
    namePlaceholder: "Sales exploration",
    storageLabel: "Persistent working space",
    empty: "No workspaces",
    emptyHint:
      "A workspace is a ready-to-use development environment: notebook, editor and project data already mounted.",
    openPending: "Starting…",
    stopTitle: "Stop workspace",
    stopWarning:
      "Everything under /mnt is kept. Running processes and files outside /mnt are lost.",
    stop: "Stop",
    launched: "Workspace launched. It will be reachable once it has started.",
    restart: "Restart",
    launchedBy: "Launched by",
    foreignHint:
      "Somebody else launched this session: it mounts their rights over the data, not yours. Launch your own.",
    egressClosed: "No direct internet access: this project's regulated data closes outbound traffic. Packages and repositories go through the internal mirrors.",
    withheld: "Not mounted, for want of a right over the data:",
    restartTitle: "Restart workspace",
    restartWarning: "The software restarts; your files and mounted data are untouched. Useful when an extension only loads at start-up. Allow a minute before reopening.",
    restarted: "The workspace is restarting.",
    stopped: "Workspace stopped.",
    notReady: "The workspace is not ready yet.",
    resources: "Resources",
  },

  jobs: {
    title: "Jobs",
    subtitle: "One-off and scheduled runs for this project.",
    create: "New job",
    createTitle: "Run a job",
    createHint:
      "A job runs a command in an environment, once or on a schedule.",
    nameLabel: "Job name",
    namePlaceholder: "Daily refresh",
    commandLabel: "Command to run",
    commandHint:
      "Runs at the project root, with /mnt and datasets already mounted.",
    commandPlaceholder: "python scripts/refresh.py",
    modeLabel: "Trigger",
    modeOnce: "Once, now",
    modeSchedule: "On a schedule",
    scheduleLabel: "Schedule",
    scheduleHint:
      "Five-field cron format: minute, hour, day of month, month, day of week.",
    timeZoneLabel: "Time zone",
    history: "Run history",
    historyHint: "The 200 most recent runs.",
    schedules: "Schedules",
    schedulesHint: "Two runs of the same schedule cannot overlap.",
    empty: "No runs",
    emptyHint: "Run a job to execute a script against the project resources.",
    emptySchedules: "No schedules",
    emptySchedulesHint:
      "Schedule a job to run it automatically, for example every morning.",
    deleteTitle: "Delete job",
    deleteWarning:
      "A running execution will be interrupted and its history removed.",
    launched: "Job started.",
    scheduled: "Schedule saved.",
  },

  apps: {
    usage: "Consultation",
    usageHint: "Who opened this application, over the last {days} days.",
    usageViews: "Views",
    usageVisitors: "Identified visitors",
    usageLastView: "Last view",
    usageAnonymous: "including {count} anonymous",
    usageEmpty: "Nobody has opened this application yet.",
    logsStarting:
      "The application is starting: its dependencies are installing. Logs will follow.",
    title: "Apps",
    subtitle: "Web applications served from this project.",
    create: "New app",
    createTitle: "Create an app",
    createHint: "Pick a framework: the launch command is generated for you.",
    frameworkLabel: "Framework",
    frameworkHint: "Determines the launch command and the default port.",
    nameLabel: "App name",
    namePlaceholder: "Sales dashboard",
    slugLabel: "Public address",
    slugHint: "Used in the app URL. Lowercase letters, digits and hyphens.",
    entrypointLabel: "Main file",
    entrypointHint: "Path relative to the project root.",
    portHint:
      "Port the app listens on. Filled in automatically from the framework.",
    commandLabel: "Launch command",
    commandHint:
      "Generated from the framework. Editable if your app needs specific options.",
    accessLabel: "Who can access the app",
    accessPrivate: "Project members only",
    accessOrganization: "My whole organisation",
    accessPublic: "Any authenticated person",
    empty: "No apps",
    emptyHint:
      "Publish a Streamlit, Dash or Shiny app to share your results without giving access to the code.",
    restart: "Restart",
    stop: "Stop",
    publish: "Publish to production",
    publishTitle: "Publish app",
    publishHint:
      "A frozen revision is created and served in production. You can roll back to an earlier revision at any time.",
    revisions: "Revisions",
    revisionsHint: "Each publication creates a restorable revision.",
    rollback: "Restore this revision",
    rollbackTitle: "Restore revision {number}",
    rollbackWarning:
      "The currently published revision will be replaced. No data is lost.",
    restarts: "Restarts",
    deleteTitle: "Delete app",
    deleteWarning: "The app will be stopped and its public address released.",
  },

  apis: {
    title: "APIs",
    subtitle:
      "Endpoints deployed from your projects, called by other applications.",
    create: "Deploy an API",
    callTitle: "Call this API",
    callHint:
      "The address, snippets ready to paste, and a token that belongs to the project — it outlives whoever created it.",
    tokenName: "Token name",
    tokenNameHint:
      'The name is how you revoke the right one without guessing: "scoring-prod" beats "token 3".',
    tokenOnce: "This token is shown once and cannot be recovered. Copy it now.",
    tokenLastUsed: "used {when}",
  },
  dashboards: {
    title: "Dashboards",
    subtitle: "Published dashboards with their own address.",
    create: "New dashboard",
    createTitle: "Create a dashboard",
    createHint:
      "A dashboard is an app dedicated to visualisation, with a stable address you can share.",
    empty: "No dashboards",
    emptyHint:
      "Create a dashboard to share results with people who do not open notebooks.",
  },

  environments: {
    buildNeedsProject: "Choose the build project above: a build spends that project's quota and is attributed to it.",
    buildProject: "Build project",
    buildProjectHint:
      "The image is built in this project and uses its quota. The environment itself stays available to every project you can access.",
    buildProjectPlaceholder: "Choose a project",
    title: "Environments",
    subtitle: "Reproducible images used by your workspaces, jobs and apps.",
    create: "New environment",
    createTitle: "Create an environment",
    createHint:
      "Start from an existing environment or a blank definition, then add your libraries.",
    nameLabel: "Environment name",
    namePlaceholder: "python-analysis",
    nameHint: "Lowercase letters, digits and hyphens.",
    baseLabel: "Starting point",
    baseEmpty: "Blank definition",
    baseHint: "Duplicating an existing environment keeps its libraries.",
    definition: "Definition",
    definitionHint:
      "The file describing the libraries installed in the environment.",
    build: "Build a revision",
    revisions: "Revisions",
    deleteRevision: "Delete this revision",
    deleteRevisionHint:
      "Removes {image} from this environment\u2019s history. The image itself stays in the registry.",
    revisionDeleted: "Revision deleted",
    systemRebuildHint:
      "This environment is the one the platform is configured to run. Rebuilding it would push into the platform\u2019s own repository, which it will not start - build under a name of your own instead.",
    revisionsHint: "Each build produces a usable revision.",
    vulnerabilities: "Vulnerabilities",
    noVulnerabilities: "None",
    notScanned: "Not scanned",
    scanUnavailable: "Unknown",
    provenance: "Source",
    provenanceUnknown: "Unknown",
    revisionNumber: "Revision {number}",
    activeRevision: "Active revision",
    empty: "No environments",
    emptyHint:
      "An environment pins the libraries your team needs, so everyone runs the same code.",
    deleteTitle: "Delete environment",
    deleteWarning:
      "Workspaces and jobs using it will no longer start. Running executions are not interrupted.",
    forkHint:
      "Add what this environment adds to the base image, for example: RUN pip install --no-cache-dir pandas",
    buildOutput: "Build output",
    buildRunning: "building…",
    buildStarted: "Build started. You can follow its progress here.",
  },

  ontologyCard: {
    title: "What this ontology describes",
    hint: "The scan counts what it finds. What the data means - what it is for, what it must not be used for, under what right it is held, who to ask - is inferable from no number of bytes: write it down.",
    declare: "Describe",
    edit: "Edit",
    undeclared:
      "Nobody has described this ontology yet. An unanswered field is information: empty beats filled with a guess.",
    placeholder:
      "What this data holds, what it is for, what it must not be used for, under what right it is held, who to ask.",
    versionLine: "Version {version}, declared by {who}.",
    measured: "What the platform measured",
    records: "Files",
    unreadable: "{count} file(s) outside the reading rule: present in the source, absent from extracts.",
    methodPathScan: "path scan",
    measuredBy: "Counted by a {method} on {when}.",
    neverMeasured: "No scan has measured this ontology yet.",
    structureNever: "File contents never checked",
    identifiersFound: "Identifiers present in the files",
    identifiersNone: "No identifier found",
    structureBy: "{method}, {count} fields checked, on {when}.",
  },
  datasets: {
    deleteWarningOntologies:
      "{ontologies} ontology(ies) describe this dataset, carrying {extracts} extract(s). They will not be deleted: they stay in the catalogue describing a source that no longer exists, and the first sign will be a scan that cannot find its bucket.",
    editTitle: "Edit the dataset",
    editHint:
      "The name and the description. Where it is stored cannot be changed here: the server does not support it yet, and the mounts already created carry the address frozen in.",
    storageFixed: "Storage (not editable)",
    storageFixedHint:
      "Endpoint and bucket. Changing them means recreating the dataset, which loses every grant made on it.",
    title: "Datasets",
    subtitle: "Data stored on S3 and shared across projects.",
    create: "New dataset",
    createTitle: "Create a dataset",
    createHint:
      "Create storage managed by the platform, or attach an existing S3 bucket.",
    modeLabel: "Storage type",
    modeLocal: "Managed by the platform",
    modeLocalHint: "The platform creates and manages the bucket for you.",
    modeExternal: "Existing S3 bucket",
    modeExternalHint: "You provide the address and access credentials.",
    classificationLabel: "Classification",
    classificationStandard: "Standard",
    classificationHds: "Health data (HDS)",
    classificationHint:
      "HDS classification restricts preview, download and export.",
    ownerOrganizationLabel: "Owning organization",
    ownerOrganizationPlaceholder: "Choose an organization",
    hdsWarning:
      "Creating and assigning HDS datasets is restricted to administrators. Preview is limited and direct download is disabled.",
    endpointLabel: "S3 service address",
    bucketLabel: "Bucket name",
    prefixHint: "Restricts the dataset to a subfolder of the bucket.",
    credentialsHint:
      "Credentials are encrypted and never shown again. Use dedicated credentials with the minimum permissions needed.",
    empty: "No datasets",
    emptyHint:
      "Create a dataset to store your files and share them with your team.",
    files: "Files",
    filesCount: "Files",
    inThisFolder: "In this folder: {files} files, {size}",
    totalSize: "Total size",
    explorer: "File explorer",
    upload: "Upload",
    dropHere: "Drop into {path}",
    dropProgress: "Uploading {done}/{total} — {name}",
    dropDone: "{count} file(s) uploaded.",
    dropPartly: "{ok} uploaded, {failed} failed:",
    uploadTitle: "Add files",
    uploadPathLabel: "Destination folder",
    uploadFileLabel: "File",
    newFolder: "New folder",
    move: "Move",
    moveDone: "{count} item(s) moved.",
    moveFailed: "Could not move: {names}",
    moveHint: "Drag a row onto a folder to move it there.",
    rename: "Rename",
    renameNewName: "New name",
    renameHint:
      "The file stays in this folder. The copy is verified before the old name is removed, so a failure leaves both names rather than neither.",
    objectRenamed: "Object renamed",
    folderNameLabel: "Folder name",
    emptyFolder: "This folder is empty",
    emptyFolderHint: "Upload a first file to get started.",
    downloadSelection: "Download selection",
    downloadDisabled:
      "This platform does not allow dataset files to be taken to a local machine. Files can be opened here.",
    deleteSelection: "Delete selection",
    selectedCount: "{count} item selected",
    selectedCountPlural: "{count} items selected",
    permissions: "Permissions",
    replaceCredentials: "Replace credentials",
    replaceCredentialsHint:
      "Tried against the bucket before being saved. Credentials that cannot reach it are refused and the current ones stay in place.",
    accessKey: "Access key",
    credentialsReplaced: "Credentials replaced",
    ownershipHint:
      "The owner administers the dataset and its permissions. An organization outlives the person who created it.",
    permissionsHint:
      "The owner manages the resource. Writer: read and write. Reader: read only.",
    grant: "Grant access",
    revoke: "Revoke access",
    storage: "Storage",
    classification: "Classification",
    deleteTitle: "Delete dataset",
    deleteWarning:
      "The dataset and its metadata are removed. For an external bucket, the files stay in your bucket.",
    deleteObjectsTitle: "Delete selected files",
    deleteObjectsWarning: "This cannot be undone.",
    deleteFoldersWarning:
      "{folders} folder(s) and {files} file(s) selected. A folder is deleted with everything inside it, including what this screen does not list. This cannot be undone.",
    uploadDone: "File uploaded.",
    uploadFailed: "Upload failed",
  },

  datasources: {
    title: "Data sources",
    subtitle:
      "Connections to databases and data services, attachable to your projects.",
    create: "New data source",
    createTitle: "Add a data source",
    createHint:
      "Connect an existing database, or deploy a service managed by the platform.",
    permissions: "Who may use this connector",
    permissionsHint:
      "Lend the connection without parting with it. A reader opens it from a workspace, an app or a job; a writer may also edit its definition.",
    grant: "Lend",
    revoke: "Revoke",
    typeLabel: "Source type",
    actLabel: "What do you want to do?",
    actHint: "Both produce a data source you can attach to a project.",
    actConnect: "Connect an existing database",
    actConnectHint: "You say where it is and how to connect to it",
    actCreate: "Create a database",
    actCreateHint: "The platform runs it and generates its credentials",
    engineLabel: "Engine",
    storageLabel: "Volume",
    storageHint: "Size of the persistent disk, for example 10Gi.",
    tierLabel: "Hardware tier",
    tierHint: "What the database gets as processor and memory.",
    manage: "Manage the data source",
    definition: "Definition",
    definitionHint: "What the source points at. Changing the host redirects everybody who mounts it, so it needs Write on it, or its ownership.",
    definitionManagedHint: "This database is run by the platform: its host, port, database, user and password are its own. Only its name changes here.",
    definitionSaved: "Definition saved",
    databaseLabel: "Database",
    usernameLabel: "User",
    passwordSecretLabel: "Secret holding the password",
    passwordSecretHint:
      "Pick an existing secret rather than typing the password here.",
    sslLabel: "SSL mode",
    empty: "No data sources",
    emptyHint:
      "Add a connection to query a database from your workspaces and jobs.",
    test: "Test connection",
    testOk: "Connection established.",
    testFailed: "Connection failed",
    restart: "Restart service",
    attached: "Attached projects",
    attach: "Attach to project",
    detach: "Detach from project",
    managed: "Managed service",
    external: "External connection",
    deleteTitle: "Delete data source",
    deleteWarning: "Attached projects will lose access to this source.",
  },

  mounts: {
    title: "Projects",
    hint: "A catalogue object mounts in as many projects as you like: it is a link, not an appartenance. Detaching removes the mount, never the object.",
    none: "—",
    attach: "Attach",
    detach: "Detach",
    attached: "Attached to the project.",
    detached: "Detached from the project.",
    noProject: "You are not a member of any project.",
    hidden: "+{count} not visible",
    hiddenHint:
      "Also mounted in {count} project(s) you are not a member of, or whose project has been deleted.",
  },

  ontologies: {
    describe: "Describe the layout",
    describePrompt:
      'Help me describe how "{name}" is laid out, from the path shapes: which level holds the grouping, which holds the time, which holds the kind - and what this trade calls those three things, one word each (patient / visit / modality, say, or client / month / operation).',
    title: "Ontologies",
    subtitle: "Your business objects above the raw data.",
    empty: "No ontology",
    emptyHint:
      "An ontology describes your business objects above the data. Noryx builds the inventory by reading a dataset\u2019s object paths - never their content: what groups them, when, and of what kind. What the data means is declared on the ontology.",
    scanTitle: "Scan a source",
    scanHint:
      "The scan reads the dataset\u2019s object paths — never their content — and infers an inventory: what groups the objects, when, of what kind, in what formats and what volume. That is a photograph, not a description: what the data means — its purpose, what it must not be used for, the right it is held under — is declared on the ontology, and the figures the platform took itself sit beside the declaration. Scanning a source that is already described updates its ontology rather than adding a second one; extracts already declared keep their file list.",
    describeSent:
      "Question sent to the assistant — open the panel to read the answer.",
    scanDataset: "Dataset",
    scanDone: "Ontology created: {objects} objects, {subjects} {entities}.",
    scanRefreshed:
      "Ontology updated: {objects} objects, {subjects} {entities}. Extracts already declared keep their file list.",
    scanNoDataset:
      "No dataset attached to this project. Attach one from the project\u2019s Data tab.",
    scan: "Scan the source",
    object: "Object",
    objects: "Objects",
    noMatch: "No object matches this filter.",
    source: "Source",
    profile: "Inference profile",
    reading: "Reading",
    readingUnrecorded: "Not recorded",
    readingUnrecordedHint: "This photograph predates the rule being recorded: how it was read is unknown. A fresh scan will say.",
    readingPending: "This photograph was not taken with the current rule.",
    readingPendingHint: "The source now declares \"{rule}\". Scan again for this page's figures and words to follow.",
    readingDeclared: "Declared",
    readingDefault: "Default",
    completeness: "Coverage",
    completenessHint:
      "How many {entities} carry each {category}, and which ones do not. An extract asked for by {category} silently excludes the latter.",
    completenessSubjects:
      "{complete} of {total} {entities} carry every {category}.",
    completenessModality: "Category",
    completenessWithout: "{entities} without this {category}",
    patternRecognisedFor: "Recognised {entities}",
    completenessHolders: "Subjects",
    completenessMissing: "Subjects without this modality",
    extracts: "Extracts",
    extractDeclare: "Declare an extract",
    extractPickOntology: "Pick an ontology",
    extractsNone: "No extract is cut from this ontology.",
    extractsCount: "{count} extract(s) are cut from this ontology.",
    extractsHint:
      "An extract is what you mount in a workspace: the ontology says what is there, the extract says what you take. It freezes the file list the moment you declare it, so the same question asked next month still designates the same study. Nothing is duplicated — mounting builds a tree of links into the dataset.",
    extractCreate: "Declare an extract",
    extractModalitiesUnknown:
      "No modality measured on this ontology — the extract will take everything.",
    layoutLevel: "Folder {rank}",
    layoutDrop: "Remove",
    layoutAdd: "Add a folder: {name}",
    layoutLeaf: "files…",
    layoutShortWarning: "A level fewer merges what it separated. If two files would land in the same place, the declaration is refused and names them.",
    extractFrom: "Cut from the ontology \"{name}\", as it was read on {when}.",
    extractFromFigures: "{entities} {entityName}, {files} files, {categories} {categoryName}.",
    extractWhat: "1 · What you take",
    extractWhatHint: "Everything, by default. Tick to narrow it.",
    extractKinds: "Kinds of file",
    extractKindsAll: "Take only these kinds. None ticked: all of them.",
    extractKindsChosen: "The other kinds stay in the source.",
    extractKindWeight: "{objects} files, {size}",
    extractOwnershipHint: "The owner decides who may read the extract and who may delete it.",
    permissions: "Who may read this ontology",
    permissionsHint: "Lend a reading of the study without transferring the ontology.",
    grant: "Lend",
    revoke: "Revoke",
    extractPermissions: "Who may read this extract",
    extractPermissionsHint: "Lend the selection without lending the study it was cut from, and without parting with it.",
    extractGrant: "Lend",
    extractRevoke: "Revoke",
    extractDataTypes: "Folders inside the modality",
    extractDataTypesAll: "Take only these folders. None ticked: all of them.",
    extractDataTypesChosen: "The other folders stay in the source.",
    extractTables: "Measurement tables",
    extractTablesAll: "Take only these tables. None ticked: all of them.",
    extractTablesChosen: "The other tables stay in the source.",
    extractPickCategories: "Take only these {categories}. None ticked: all of them.",
    extractPickEntities: "Take only these {entities}, comma-separated. Empty: all of them.",
    extractTakes: "This extract will freeze {files} of {total} files.",
    extractTakesAtMost: "This extract will freeze at most {files} of {total} files — the entity filter narrows it further.",
    extractHow: "2 · How you find it again",
    extractHowHint: "The order of folders in the workspace. The same files, arranged for the question you are asking.",
    layoutUnnamed: "extract-name",
    extractLayout: "Layout",
    pattern: "How the source is read",
    scanDeclareRule: "No subject recognised: declare this dataset's reading rule, under Settings then Reading rule. Shapes found:",
    patternRule: "Reading rule",
    scanChangedTitle: "What changed",
    scanChangedReading:
      "The reading of the source changed: {objectsBefore} → {objectsAfter} objects, {subjectsBefore} → {subjectsAfter} {entities}. The rule explains the difference, not the data.",
    scanChangedData:
      "The source moved, with the reading unchanged: {objectsBefore} → {objectsAfter} objects, {subjectsBefore} → {subjectsAfter} {entities}.",
    patternRuleDefaultPlain: "Default rule: the first level that looks like an identifier, then the next two",
    patternLevelIs: "level {level} = {name}",
    patternShapesMore: "Show the {count} other shapes",
    patternShapesFewer: "Collapse",
    settings: "Settings",
    settingsHint: "How the source is read, object exploration, and who owns it.",
    scanHistory: "Scan history",
    scanHistoryHint: "Every photograph of the source, newest first, with the rule that produced it. A difference is either the data moving or the reading changing.",
    scanWhen: "Scanned",
    scanChanged: "Change",
    scanFirst: "First scan",
    scanUnchanged: "Nothing moved",
    scanDelta: "{count} {what}",
    scanReadingChanged: "Reading changed",
    content: "Explore the content",
    contentHint: "One row per {entity} × {period} × {category}, with the file count. This is the detail behind the figures on the card.",
    contentFilter: "Filter",
    contentFilterHint: "Searches the same word across the three columns. Empty shows everything.",
    contentApply: "Filter",
    contentEmpty: "This ontology recognised no file.",
    contentCount: "{shown} of {total} row(s) shown.",
    proposalPaste: "Paste the assistant's proposal",
    proposalPasteHint: "Paste its whole answer. The three levels and the three words fill the form below — nothing is stored until you store it.",
    proposalApply: "Read the proposal",
    proposalRead: "Levels read: {levels} — words: {names}. Check it, try it, then store it.",
    proposalNoNames: "none",
    proposalUnreadable: "No reading found. Expected \"subject: level 1, visit: level 2, modality: level 3\", and if possible \"names: patient / visit / modality\".",
    patternProducedWith: "Produced with the rule:",
    scanRuleDeclared: "This dataset declares its reading:",
    scanRuleWhereToChange:
      '— change it on the ontology, under "How the source is read".',
    scanRuleDefault: "This dataset declares no reading.",
    scanRuleDefaultHint:
      "The default rule applies: it looks for a segment that resembles a subject identifier, then takes the next two as the visit and the modality. If the scan recognises nothing, declare the reading on the ontology and scan again.",
    patternAsk: "Ask for a proposal",
    patternSubject: "Level 1 read",
    patternVisit: "Level 2 read",
    patternModality: "Level 3 read",
    patternRuleHint:
      "A path level, counted from zero. For `SELENA/SELENA-01-001/20260218/ANTERION/…`: subject 1, visit 2, modality 3. Leave a level empty when the source does not carry it. Try it before you store it.",
    patternCompiled: "The platform’s default rule",
    patternNamesHint: "What this trade calls its three levels. Left empty, the platform says entity, period and category.",
    patternSubjectName: "Grouping name",
    patternVisitName: "Time level name",
    patternModalityName: "Category name",
    patternSubjectLevel: "Grouping level",
    patternVisitLevel: "Time level",
    patternDataTypeLevel: "Data type level (optional)",
    patternDataTypeHint: "The folder below the modality. Empty: the modality is the last level.",
    patternDataTypeName: "Data type name",
    patternDataTypeWord: "data type",
    patternModalityLevel: "Category level",
    patternTry: "Try it on real paths",
    patternSave: "Store the rule",
    patternSaved: "Rule stored. Rescan to apply it.",
    patternClear: "Forget the rule",
    patternCleared: "Rule forgotten: the source goes back to the default rule.",
    patternTrialResult: "The rule recognises {recognised} of the {sampled} paths tried. Here is what it reads on each — a level off by one shows up here, not after the scan.",
    extractWhole: "Mount the whole ontology",
    extractWholeSuffix: "everything",
    extractWholeHint:
      "An ontology describes a source, it does not contain it: what mounts is always an extract. This is the special case with no filter - the whole ontology.",
    patternHint:
      "How the platform reads this source’s paths: which position holds the subject, the visit, the modality. This is what the assistant proposes when shown the shapes.",
    patternRecognised: "Shapes that produced a subject",
    patternUnrecognised: "Shapes that produced nothing",
    patternNone:
      "No shape recognised: this source is not laid out the way the profile expects. Ask the assistant for a reading.",
    patternDirectories:
      "{count} directory key(s) ignored: S3 has no directories, and counting them would inflate the n.",
    extractEmpty: "No extract for this ontology.",
    extractCatalogEmpty: "No extract",
    extractCreated: "Extract frozen over {count} files.",
    extractDeleteTitle: "Delete the extract",
    extractDeleteWarning:
      "The frozen selection is lost. The dataset files are untouched.",
    extractDelete: "Delete extract",
    extractRename: "Rename extract",
    level_subject: "subject",
    level_visit: "visit",
    level_modality: "modality",
    extractDetailHint:
      "The file list frozen when the extract was declared. It is what makes an n reproducible two years later.",
    extractWholeBadge: "The whole ontology",
    extractSubjectCount: "{count} subject(s)",
    extractFilesShown: "{shown} files shown out of {total}.",
    extractCheckFiles: "Check the files",
    extractCheckHint:
      "Freezing protects the n, not the bytes: a file deleted from the source mounts as a dangling link.",
    extractChecking: "Checking…",
    extractCheckFailed: "The source could not be read.",
    extractCheckAllPresent:
      "Sample of {checked} files out of {total}: all present.",
    extractCheckMissing:
      "{missing} file(s) missing out of the {checked} checked - the source has been pruned.",
    extractRenameHint:
      "The label alone changes. The frozen file list, its author and its dates do not move: an n already published under this name still describes the same files.",
    extractDeleted: "Extract deleted.",
    extractCatalogHint:
      "An extract is a frozen subset of an ontology: a file list settled at the moment it is declared. It is handed on and attached to a project like a dataset. Declare one from the ontology it is drawn from.",
    extractMountHint:
      "Mounted in the project workspaces under /extracts/<name>/, with the levels in the order this extract declared.",
    freshness: "Freshness",
    freshnessMeasured: "Measured {days} days ago, over {objects} objects.",
    freshnessMeasuredToday: "Measured today, over {objects} objects.",
    freshnessAligned: "The source still holds the same number of objects.",
    freshnessGrown:
      "The source holds {drift} more objects since this measurement.",
    freshnessShrunk:
      "The source holds {drift} fewer objects since this measurement.",
    freshnessUnknown:
      "Source not reached: cannot tell whether this measurement still holds.",
    freshnessStale: "This ontology no longer describes its source",
    ownershipHint:
      "The owner administers the ontology and its permissions. An organization outlives the person who created it.",
    rename: "Name and description",
    renameHint:
      "The name comes from the scan, which reads it off the source. Correcting it touches neither the scanned objects nor the extracts: only what is displayed changes.",
    deleteTitle: "Delete the ontology",
    deleteWarning: "Attached projects will lose access to this model.",
    deleteWarningExtracts:
      "This ontology carries {count} extract(s): they will be destroyed with it. A frozen selection cannot be rebuilt identically - a new scan over a source that has grown gives a different n. To read the source again, scan it instead: that refreshes the ontology without deleting it.",
    deleteWarningUnknown:
      "What this deletion takes with it could not be read. Any extract declared on this ontology would be destroyed with it.",
  },

  repositories: {
    createdUnreachable: "Repository added, but the check failed",
    title: "Git repositories",
    subtitle: "Repositories cloned into your workspaces under /repos.",
    create: "New repository",
    createTitle: "Add a Git repository",
    createHint:
      "The repository is cloned automatically into /repos when your workspaces start.",
    urlLabel: "Repository address",
    tokenTooBroad: "This token can also:",
    tokenTooBroadDanger: "This token can destroy or administer:",
    urlHint:
      "HTTPS address of the repository. Authentication is by token; SSH addresses are not supported yet.",
    defaultRefLabel: "Default branch",
    authTypeLabel: "Authentication",
    authPersonalToken: "Personal access token",
    authRepositoryToken: "Repository access token",
    authNone: "Public repository",
    authSecret: "Token stored in a secret",
    authSecretLabel: "Secret holding the token",
    authorNameLabel: "Name for commits",
    authorEmailLabel: "Email for commits",
    empty: "No Git repositories",
    emptyHint:
      "Add a repository to find your code in every workspace of the project.",
    validate: "Check access",
    reachable: "Reachable",
    unreachable: "Unreachable",
    lastValidated: "Last checked",
    deleteTitle: "Delete repository",
    deleteWarning:
      "The repository will no longer be cloned into new workspaces.",
  },

  secrets: {
    title: "Secrets",
    subtitle: "Sensitive values injected into your runs.",
    create: "New secret",
    createTitle: "Create a secret",
    createHint:
      "The value is encrypted and will never be shown again after saving.",
    nameLabel: "Secret name",
    nameHint: "Injected into your runs as NORYX_SECRET_{name}.",
    namePlaceholder: "partner_api",
    valueLabel: "Value",
    valueHint: "It will not be shown again after saving.",
    kindLabel: "Kind of secret",
    kindHint:
      "A token has a lifetime decided elsewhere; the platform warns before it expires, provided it knows the date.",
    kindGeneric: "Any value",
    kindPersonalToken: "Personal access token",
    kindRepositoryToken: "Repository access token",
    expiresRequiredHint: "GitHub and GitLab both cap their tokens at a year.",
    neverExpires: "This token does not expire",
    expiryAnswerRequired:
      "Give the expiry date, or tick that the token does not expire.",
    expiresLabel: "Expiry date",
    expiresHint: "Optional. An expired secret is no longer injected into runs.",
    empty: "No secrets",
    emptyHint:
      "Store your tokens and passwords here instead of writing them into your code.",
    expiringSoon: "Expiring soon",
    deleteTitle: "Delete secret",
    deleteWarning: "Runs that use it will fail on their next start.",
    created: "Secret saved.",
  },

  production: {
    title: "Production",
    subtitle: "Inventory of services currently published on the platform.",
    published: "Published services",
    running: "Running",
    public: "Broadly accessible",
    revision: "Revision",
    access: "Access",
    publishedAt: "Published",
    publishedBy: "Published by",
    empty: "No published services",
    emptyHint: "Publish an app from a project to see it appear here.",
    revisionHistory: "Revision history",
    selectService: "Select a service to see its revisions.",
  },

  members: {
    orgTitle: "Organization access",
    orgHint:
      "Every member of the organization holds this role on the project. Access adds up: the stronger of a personal role and one inherited from an organization applies.",
    orgEmpty: "No organization has access to this project.",
    orgPlaceholder: "Choose an organization",
    orgGrant: "Grant",
    orgGranted: "Access granted to the organization.",
    title: "Members",
    subtitle: "People with access to this project and their permission level.",
    invite: "Invite",
    inviteTitle: "Invite someone",
    inviteHint: "The person must already have an account on the platform.",
    userLabel: "User",
    roleViewer: "Viewer",
    roleBasedOn: "Inherits from {base}",
    roleViewerHint: "Reads the project and its results, changes nothing.",
    roleEditor: "Editor",
    roleEditorHint: "Launches workspaces and runs, edits resources.",
    roleAdmin: "Admin",
    roleAdminHint: "Manages members and project settings.",
    empty: "No members",
    emptyHint: "Invite your collaborators to work together on this project.",
  },

  network: {
    title: "Network and security",
    subtitle: "Outbound internet access requested by projects and users.",
    profileLabel: "Access profile",
    destinationLabel: "Destination",
    justificationLabel: "Justification",
    approve: "Approve",
    reject: "Reject",
    empty: "No requests",
    emptyHint: "Outbound internet access requests will appear here for review.",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
  },

  access: {
    title: "Access",
    rule: "Owning administers, writes and shares; Write and Read are grants on the object, to a person, a team or an organization. Belonging to an organization gives nothing by itself.",
    connectorHint: "Read: run it in a workspace and see who else holds it. Write: also change what it points at, which redirects everybody who mounts it.",
    frozenHint: "An extract is frozen: it is read, never written, even by someone holding Write on the data. Changing a file goes through the source dataset, attached to the project. The owner shares and deletes the extract.",
    owner: "Owner",
    ownerMeans: "administers, writes, shares",
    ownerOrganizationMeans: "its named administrators administer, write and share; its members hold only what is granted to them",
    right: "Right",
    grant: "Give access",
    revoke: "Withdraw access",
    empty: "No grant",
    emptyHint: "Only the owner reaches it. Give access to a person, a team or an organization.",
  },
  rbac: {
    matrix: "Permission matrix",
    matrixHint:
      "Who may do what, object by object. Owning administers (transfer, grant, delete) and does not grant reading; reading or writing is a grant on the object, to a person, a team or an organization; an “inherited” row comes from such a grant made to a group. What a project attaches is not listed here: attaching confers no right on the data.",
    subject: "Subject",
    resource: "Resource",
    empty: "No permissions recorded",
    emptyHint: "Permissions granted on projects and datasets will appear here.",
    organizations: "Organisations",
  },

  activity: {
    title: "Activity",
    subtitle:
      "Executions currently running on the platform, across all projects.",
    executions: "Active executions",
    pods: "Pods",
    cpuReserved: "Reserved CPU",
    memoryReserved: "Reserved memory",
    empty: "Nothing running",
    emptyHint: "Active workspaces, jobs and apps will appear here.",
    stopTitle: "Stop execution",
    stopWarning: "The execution will be interrupted immediately.",
    kind: "Type",
  },

  quota: {
    title: "Project quota",
    hint: "What this project may run at the same time. A launch beyond it is refused, naming the limit reached.",
    unlimited:
      "No limit set: this project can launch as much as the cluster allows.",
    vcpu: "vCPU",
    memory: "Memory",
    workspaces: "Workspaces",
    jobs: "Jobs",
    noLimit: "unlimited",
    zeroMeansNoLimit: "0 means no limit on this dimension.",
  },
  usage: {
    consumption: "Consumption",
    consumptionHint:
      "What the project actually consumed, sampled every five minutes and kept for a hundred days.",
    restsOn: "Total built from {samples} measurements.",
    title: "Consumption by project",
    hint: "What each project actually consumed over the last 30 days. Sampled every 5 minutes; a platform outage is not billed.",
    vcpuHours: "vCPU-hours",
    memoryHours: "GiB-hours",
    peak: "Peak",
    samples: "Samples",
  },
  people: {
    membershipGrantLabel: "What belonging gives on what the organization owns",
    membershipGrantHint: "Written as a grant to the organization when an object is created for it or transferred to it; never retroactive. “Nothing”: its named administrators decide object by object.",
    membershipGrantNone: "Nothing",
    membershipGrantReader: "Read",
    membershipGrantWriter: "Read and write",
    membershipGrantSaved: "Policy saved",
    serviceAccount: "Service account",
    serviceAccountHint:
      "This account is not a person: no password, no sign-in, it acts through a token.",
    title: "People & organisations",
    hint: "Who is who: each organisation, its teams, and the people in them. Everything is edited from the right-hand panel.",
    newOrganization: "New organisation",
    newOrganizationHint:
      "An organisation lives in the directory: it is created here and not deleted here.",
    organizationCreated: "Organisation created",
    alias: "Alias",
    organizationSummary: "{people} person(s), {teams} team(s)",
    newTeam: "New team",
    administrators: "Organization administrators",
    administratorsHint:
      "Who may transfer, rename and delete what the organization owns, and decide who reads it. A right, not a membership: administering opens no file.",
    administratorsEmpty:
      "Nobody is named: what this organization owns is administered only by platform administrators.",
    addToOrganization: "Add to organisation",
    removeFromOrganization: "Remove from organisation",
    teamSummary: "{count} member(s)",
    teamRenamed: "Team renamed",
    teamRemoveWarning:
      "Delete team {name}? Its {count} member(s) lose the access it gave them, and its project grants go with it.",
    teamRemoveHint:
      "{count} member(s) affected. The team's project grants are removed with it.",
    unaffiliated: "No organisation",
    unaffiliatedHint:
      "Accounts that belong to no organisation - typically service accounts, or people not attached yet.",
    nobody: "Nobody.",
    deactivated: "Deactivated",
    neverSeen: "Never signed in",
    viewTree: "Tree",
    viewList: "List",
    firstSeen: "First sign-in",
    presence_active: "Active: has signed in",
    presence_never: "Never signed in",
    presence_disabled: "Deactivated",
    administratorHint: "Can act on everybody's work.",
    createUserIn: "The account will be created in {organization}.",
  },
  catalog: {
    subtitle:
      "What is shared across projects — datasets, sources, ontologies, repositories, environments. A project attaches what it needs from here.",
  },
  projectData: {
    subtitle:
      "What this project has attached from the catalogue. Nothing is copied: detaching deletes nothing.",
  },
  serviceAccounts: {
    title: "Service accounts",
    subtitle:
      "Accounts that are not people: they belong to an organization, hold roles, own what must outlive whoever set it up, and never sign in.",
    createTitle: "Create a service account",
    createHint:
      "No password and no sign-in: its credentials are tokens, issued here.",
    nameHint: "The name the audit will record it under.",
    namePlaceholder: "for-production",
    purpose: "What it does",
    purposePlaceholder: "Publishes the study dashboards",
    responsible: "Responsible",
    responsibleHint: "The person who answers for this account. Required.",
    noResponsible: "nobody",
    credentials: "Live tokens",
    noCredential: "none",
    issue: "Issue a token",
    nextCredential: "Scope of the next token",
    created: "Service account created",
    disableTitle: "Disable",
    disableWarning:
      "{name} will stop acting: its tokens are revoked, then the account is disabled. What it owns stays its own, and the audit keeps its trail.",
    disabled: "Account disabled, {count} token(s) revoked",
    empty: "No service account",
    emptyHint:
      "A service account carries a production nobody should have to inherit the day its author leaves.",
  },

  componentCredentials: {
    rotate: "Renew",
    rotateWarning:
      "A new secret is issued for {name} and the old one stops working at once. The new one is shown only once.",
    howToTitle: "How to use it",
    howToHint:
      "The secret goes in the authorization header. It will not be shown again after this page.",
    identityHint:
      "A service account is not a user: it has no name and no account, it acts as the platform, and its scope is what limits it. The audit records it under the component name.",
    scopeFullWarning:
      "The full scope limits nothing: this account will be able to do anything on the platform.",
    title: "Service accounts",
    subtitle:
      "The credentials automation authenticates with: backups, validation, continuous integration. They belong to nobody and are revoked one at a time.",
    component: "Component",
    componentHint:
      "The name of the program using it. It is what the audit trail will show.",
    componentPlaceholder: "nightly-backup",
    scopeHint:
      "One scope per account. A component asking for two is two accounts, and the audit stays readable.",
    createTitle: "Create a service account",
    createHint: "The secret is shown once, at creation.",
    lastUsed: "Last used",
    state_active: "active",
    state_idle: "never used",
    state_expired: "expired",
    state_revoked: "revoked",
    revokeWarning:
      "{name} will stop authenticating the moment this is revoked. Nothing will warn it.",
    empty: "No service account",
    emptyHint:
      "The platform's own components and your automation authenticate with these credentials.",
  },

  admin: {
    visibleAsAdmin: "admin",
    visibleAsAdminHint:
      "On your screen because you administer the platform: you hold no grant of your own on it.",
    deleteAccount: "Delete account",
    deleteHint:
      "Deleting removes the account from the identity provider for good. It cannot be undone: re-enabling will no longer be possible.",
    deleteSecrets:
      "Its personal secrets are deleted: they belong to a person, not to a role, and are not inherited.",
    deleteAudit:
      "The audit trail keeps the events this account produced, under its identifier.",
    deletedHint: "Account deleted, {count} personal secret(s) removed.",
    deactivate: "Disable account",
    deactivateHint:
      "The account can no longer sign in. It is not deleted: the audit trail stays attributable, and the account can be re-enabled.",
    deactivateTokens:
      "Its API tokens are revoked — a token acts as its owner, so an account disabled while its tokens work is not disabled.",
    deactivateSecrets:
      "Personal secrets are not transferred: they belong to the person, not to the role.",
    deactivatedHint: "Account disabled, {count} token(s) revoked",
    reactivate: "Re-enable",
    ownedTitle: "What this account owns",
    ownsNothing:
      "This account owns nothing personally: there is nothing to hand on.",
    ownedProjects: "Projects:",
    ownedDatasets: "Datasets:",
    ownedOntologies: "Ontologies:",
    ownedApps: "Apps:",
    ownedDatasources: "Data sources:",
    ownedRepositories: "Repositories:",
    ownedExtracts: "Extracts:",
    successorLabel: "Successor",
    successorHint:
      "Receives ownership of everything above, in the same operation.",
    smtp: "Mail server",
    smtpHint:
      "Used to send invitation and reset links. Filled in per installation: each site sends through its own relay.",
    smtpConfigured: "Configured",
    smtpMissing: "Not set",
    smtpFrom: "Sender address",
    smtpFromHint:
      "What the recipient sees. The relay must accept this address.",
    smtpFromName: "Display name",
    smtpReplyTo: "Reply to",
    smtpUser: "Username",
    smtpPassword: "Password",
    smtpPasswordHint: "Write-only: it is never sent back to a browser.",
    smtpPasswordStored: "A password is stored. Leave empty to keep it.",
    smtpNoAuthHint:
      "Authentication is off: the relay accepts mail without credentials.",
    smtpAuth: "Authentication",
    smtpTest: "Send a test",
    smtpTestTo: "Test recipient",
    smtpTestHint:
      "Test before saving: the configuration in place is not replaced.",
    smtpTestSent: "Test message sent to {recipient}",
    resetByEmail: "Email a link",
    activity: "Activity",
    activityHint:
      "Who used the platform, doing what. Counted from what the platform recorded, not from what anyone remembers.",
    activityEmpty: "Nothing recorded over this period.",
    activityCoverage:
      "Records begin on {since}; the period before that is not quiet, it is unrecorded.",
    byOrganization: "By organisation",
    noOrganization: "No organisation",
    activePeoplePerDay: "People active per day",
    whoWasActive: "{count} people active",
    whatWasDone: "What was done",
    peopleCount: "{count} people",
    eventsCount: "{count} actions",
    actionsTruncated:
      "Showing the {shown} most frequent of {total} kinds of action.",
    window7d: "Last 7 days",
    window30d: "Last 30 days",
    window90d: "Last 90 days",
    administrator: "Admin",
    lastSeen: "Last seen",
    resetByEmailHint:
      "Sends {user} a link to choose their own password, valid for {hours} hours. Nothing to dictate or paste.",
    resetByEmailSent: "Link sent",
    teamUsage: "Consumption by team",
    teamUsageHint:
      "Over the last 30 days. Consumption is sampled per project, so a team figure is an attribution rather than a measurement.",
    teamUsageEmpty: "No usage report available.",
    teamUsageNoTeams:
      "No team reaches a project yet, so everything is unattributed.",
    teamUsagePlatform: "Platform (vCPU·h)",
    teamUsagePlatformHint: "What the cluster actually consumed.",
    teamUsageAttributed: "Attributed (vCPU·h)",
    teamUsageAttributedHint: "The sum of the rows below.",
    teamUsageOverlap:
      "Exceeds the platform by {excess} because teams share projects.",
    teamUsageUnattributed: "Unattributed (vCPU·h)",
    teamUsageUnattributedHint:
      "Reached by no team - held personally or by an organization.",
    teamUsageMembers: "Members",
    teamUsageProjects: "Projects",
    teamUsageVcpuHours: "vCPU·h",
    teamUsagePeak: "Peak vCPU",
    teamUsageShared: "{count} shared",
    teams: "Teams",
    teamCreated: "Team created",
    teamRemove: "Delete team",
    teamRemoved: "Team deleted",
    teamMemberAdd: "Add a member",
    teamMemberPlaceholder: "Pick a person",
    teamMemberRemove: "Remove from team",
    teamNoMembers: "Nobody in this team yet.",
    hardwareTiers: "Hardware tiers",
    hardwareTiersHint:
      "The sizes offered when launching a workspace, a job or an app.",
    tierName: "Display name",
    tierId: "Identifier",
    tierIdHint:
      "Referenced by the API and by existing workspaces: it does not change.",
    tierRequest: "Reserved",
    tierLimit: "Limit",
    tierRequestHint: "What the scheduler sets aside for the machine.",
    tierLimitHint: "What the machine may reach.",
    tierDefault: "Default",
    tierAdd: "Add a tier",
    tierSaved: "Tier saved",
    tierRemoved: "Tier removed",
    tierRemoveConfirm:
      "Remove this tier? Workspaces already running keep going; it simply disappears from the choices offered.",
    tierRunningUnaffected:
      "Editing a tier has no effect on workspaces already running: the change applies to the next start.",
    title: "Administration",
    subtitle: "Platform supervision and configuration.",
    overview: "Overview",
    dataUsage: "Data usage map",
    dataUsageHint:
      "Who owns the datasets, who has access, which projects consume them and which runs are attached.",
    exportCsv: "Export as CSV",
    audit: "Audit",
    auditHint: "Log of actions performed on the platform.",
    auditEmpty: "No events",
    auditComponent: "component",
    auditPlatform: "platform",
    auditDetails: "Detail",
    auditEmptyHint: "Actions performed on the platform will be recorded here.",
    actor: "Actor",
    action: "Action",
    outcome: "Outcome",
    occurredAt: "Date",
    storageEndpoints: "Storage endpoints",
    storageEndpointsHint: "S3 services declared for datasets and backups.",
    testEndpoint: "Test",
    backups: "Backups",
    backupsHint: "External S3 target used for platform backups.",
    backupConfigure: "Configure target",
    backupRun: "Run a backup",
    backupHistory: "History",
    backupEmpty: "No backups",
    backupEmptyHint: "Configure an S3 target then run a first backup.",
    backupIncomplete:
      "The latest backup completed with caveats. Its contents are incomplete.",
    backupNotConfigured: "No target configured",
    backupConfigured: "Target configured",
    bucket: "Bucket",
    encryptionKey: "Encryption key",
    lastUpdated: "Last updated",
  },

  graph: {
    title: "Access graph",
    focusSubject: "Focus on a person or organization",
    focusResource: "Focus on a resource",
    resourceType: "Resource type",
    everyone: "Everyone",
    everything: "Every resource",
    allTypes: "All types",
    showInherited: "Show inherited access",
    hideInherited: "Hide inherited access",
    clear: "Reset",
    empty: "No access matches this filter.",
    tooMany: "{count} grants to draw.",
    tooManyHint:
      "Choose a person, a resource or a type: beyond this the graph is unreadable and answers nothing.",
    legendDirect: "Direct access",
    legendInherited: "Inherited from an organization",
    edges: "{count} grants shown",
  },

  inventory: {
    title: "Software and licences",
    subtitle:
      "What the platform ships, with each component's licence. Generated from the resolved dependency graph rather than maintained by hand.",
    component: "Component",
    version: "Version",
    licence: "Licence",
    partOf: "Part of",
    source: "Source",
    origin_detected: "Read",
    origin_declared: "Declared",
    origin_unresolved: "Unresolved",
    count: "{count} components",
    unknownCount: "{count} licence(s) undetermined",
    allKnown: "Every licence is determined",
    generated: "Generated",
  },

  identity: {
    createUser: "Create an account",
    editUser: "Edit",
    editUserTitle: "Correct this account",
    editUserHint:
      "Fixes a name or an email typed wrong. The username cannot change: it is how Noryx identifies this person on every project, team and audit entry.",
    usernameFixed: "Username (cannot be changed)",
    userUpdated: "Account updated",
    username: "Username",
    firstName: "First name",
    lastName: "Last name",
    resetPassword: "Reset",
    resetPasswordHint:
      "A new temporary password will be generated for {user}. The old one stops working immediately.",
    passwordHint:
      "Shown once and not recoverable. Pass it on; it must be changed at first sign-in.",
    passwordCopied: "Password copied.",
  },

  adminNav: {
    modules: "Modules",

    people: "People and access",

    resources: "Resources",

    operations: "Operations",

    compliance: "Compliance",

    configuration: "Configuration",
  },

  projectVariables: {
    title: "Environment variables",

    hint: "The settings the work needs, shared by everyone on the project and injected into workloads under their own names. For a personal credential use a secret: that follows you, these follow the project.",

    add: "Add a variable",

    nameHint:
      "Letters, digits and underscores, as a shell wants them. Names starting with NORYX_ belong to the platform.",

    valueColumn: "Value",

    descriptionHint: "What it is for, for whoever reads it in six months.",

    updatedColumn: "Updated",

    hidden: "Hidden",

    saved: "Variable saved.",

    removed: "Variable removed.",

    emptyTitle: "No variables",

    emptyHint:
      "Add a tracking server address, a bucket name, a registry URL - whatever your workloads expect in their environment.",

    formHint:
      "The value is encrypted at rest and readable by the members who can launch workloads.",

    removeTitle: "Remove the variable",

    removeHint:
      "{name} will no longer be injected into this project\u2019s workloads.",
  },

  account: {
    title: "My account",
    subtitle: "Your API credentials, your secrets and your preferences.",
  },

  tokens: {
    scopeLabel: "Scope",
    scopeHint: "What the token may do, within your own rights.",
    scopeRead: "Read only",
    scopeReadHint: "Look, change nothing.",
    scopeDatasets: "Datasets",
    scopeDatasetsHint:
      "Upload and replace files in the datasets this account can already reach. Starts nothing and deletes no project.",
    scopeWorkspaces: "Workspaces",
    scopeWorkspacesHint: "Start and stop workspaces, plus reading.",
    scopeJobs: "Jobs",
    scopeJobsHint: "Run jobs and builds — the continuous integration case.",
    scopeFull: "All your rights",
    scopeFullHint: "No restriction: the token may do anything you can.",
    title: "API tokens",
    subtitle:
      "For calling the API outside a browser: continuous integration, a notebook, a script.",
    createHint:
      "The token acts as you and holds no rights of its own: it can do nothing you cannot already do.",
    nameHint: "What will let you revoke the right one later.",
    namePlaceholder: "gitlab-ci",
    expiryDays: "{days} days",
    expiryNever: "No expiry",
    issuedTitle: "Your token",
    issuedHint: "It is shown once and is not recoverable. Copy it now.",
    copied: "Token copied.",
    dismiss: "Copied",
    empty: "No tokens yet.",
    expires: "expires",
    lastUsed: "used",
    revoked: "Revoked",
  },

  health: {
    history: "History",
    historyHint:
      "Technical conditions the platform observed about itself. Job failures are not here: they belong to whoever ran them.",
    historyEmpty: "No condition recorded over this period.",
    notRecording: "History is not recorded on this installation.",
    ongoing: "Ongoing",
    resolvedAfter: "Resolved after",
    raised: "Raised",
    last30: "30 days",
    last90: "90 days",
    last365: "1 year",
    title: "Platform health",
    subtitle: "Conditions that need attention.",
    checkedAt: "Checked",
    statusHealthy: "All good",
    statusDegraded: "Degraded",
    statusCritical: "Critical",
    allClear: "No problem detected.",
    indicator: "Platform alerts",
  },
  settings: {
    key_alert_email: "Alert mailbox",
    title: "Platform settings",
    subtitle: "Parameters changeable without a redeployment.",
    precedence:
      "A value set here overrides the environment variable, which overrides the default. Clearing a field restores the inherited value.",
    saved: "Setting saved.",
    unset: "Not set",
    key_workspace_max_lifetime: "Maximum workspace lifetime",
    key_alert_webhook_url: "Alert webhook",
    key_alert_instance_name: "Instance name",
    key_ui_default_theme: "Default theme",
    key_platform_backend_version: "Backend version",
    key_platform_edition: "Edition",
    key_platform_namespace: "Kubernetes namespace",
  },
  search: {
    title: "Search",
    open: "Search",
    placeholder: "Search…",
    hint: "Type at least two characters.",
    empty: "No results.",
    kind_project: "Project",
    kind_dataset: "Dataset",
    kind_datasource: "Data source",
    kind_ontology: "Ontology",
    kind_extract: "Extract",
    kind_repository: "Repository",
    kind_secret: "Secret",
    kind_workspace: "Workspace",
    kind_job: "Job",
    kind_app: "App",
  },
  errors: {
    notFoundTitle: "Page not found",
    notFoundHint: "The link you followed does not exist, or no longer exists.",
    backHome: "Back to home",
    crashHint:
      "The interface hit an unexpected problem. Reload the page; if it persists, contact your administrator.",
    reload: "Reload the page",
    projectNotFound: "Project not found",
    projectNotFoundHint:
      "This project no longer exists, or you no longer have access to it.",
    sessionExpiredTitle: "Your session has expired",
    sessionExpiredHint:
      "Sign in again to carry on. Work in your workspaces is unaffected: they keep running.",
    signedOutTitle: "Session expired",
    signedOutHint: "Sign in again to resume your work.",
    noOrganizationHint:
      "Your account is authenticated but is not attached to any organisation. Contact a platform administrator.",
  },
};
